import React from 'react';
import { INPUT_TYPE } from '@app/consts/component_consts';
import { useFormController } from '@app/hooks/useFormController';
import { LANGUAGE_DEFAULT } from '@app/modules/i18n-locales/i18n';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';

type FormInputTextProps = {
  name: string;
  placeholder?: string;
  type: keyof typeof INPUT_TYPE;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  value?: string;
  inputStyle?: React.CSSProperties;
  className?: string;
  isShowErrorMsgOnHover?: boolean;
  onClick?: () => void;
  numericOnly?: boolean;
};

const FormInputText: React.FC<FormInputTextProps> = ({
  name,
  placeholder,
  type,
  label,
  required = false,
  disabled = false,
  value,
  inputStyle,
  className,
  isShowErrorMsgOnHover = false,
  onClick,
  numericOnly = false,
}) => {
  const { field, fieldState } = useFormController({ name });
  const { i18n } = useTranslation();
  const isEn = i18n.language !== LANGUAGE_DEFAULT;

  const currentValue = value ?? field.value;
  const showClear = !!currentValue && !disabled && type !== INPUT_TYPE.search;

  const handleClear = () => {
    field.onChange('');
  };

  return (
    <div className={`flex flex-column field-name${name}${className ? ' ' + className : ''}`}>
      {label && (
        <label htmlFor={name} className="block body-sx fw-400 mb-1">
          {label}
          {required && <span style={{ color: 'var(--color-danger)', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <InputText
          {...field}
          id={name}
          invalid={fieldState.invalid}
          tooltip={isShowErrorMsgOnHover ? fieldState?.error?.message : undefined}
          tooltipOptions={
            isShowErrorMsgOnHover ? { className: 'custom-tooltip data-table-tooltip', position: 'top' } : undefined
          }
          // Render email type as "text" to bypass browser email sanitization algorithm.
          // Native type="email" causes the browser to sanitize node.value before React reads it,
          // which breaks React's controlled input reconciliation and prevents trimStart() from working.
          // We use autoComplete="email" and inputMode="email" to preserve UX (browser suggestions + mobile keyboard).
          type={type === INPUT_TYPE.email ? INPUT_TYPE.text : type}
          autoComplete={type === INPUT_TYPE.email ? 'email' : undefined}
          inputMode={type === INPUT_TYPE.email ? 'email' : numericOnly ? 'numeric' : undefined}
          placeholder={placeholder}
          disabled={disabled}
          value={currentValue}
          onChange={(e) => {
            const rawVal = numericOnly ? e.target.value.replace(/\D/g, '') : e.target.value;
            // Prevent leading whitespace while typing
            const val = rawVal.trimStart();
            if (val === e.target.value) {
              field.onChange(e);
            } else {
              e.target.value = val;
              field.onChange(val);
            }
          }}
          onBlur={(e) => {
            field.onChange(e.target.value.trim());
            field.onBlur();
          }}
          style={{ ...inputStyle, ...(showClear || type === INPUT_TYPE.search ? { paddingRight: '28px' } : {}) }}
        />
        {showClear && (
          <Button
            icon="pi pi-times"
            className={`p-button-text btn-clear${isEn ? ' btn-clear-en' : ''}`}
            onClick={handleClear}
            type="button"
            aria-label="Clear"
          />
        )}
        {type === INPUT_TYPE.search && (
          <Button
            icon="pi pi-search"
            text
            className="text-color-neutral-30 btn h-[28] p-1 absolute top-0 right-0"
            aria-label="Search"
            onClick={onClick}
          />
        )}
        {fieldState.invalid && <small className="p-error body-sx mt-1">{fieldState?.error?.message}</small>}
      </div>
    </div>
  );
};

export { FormInputText };
